import { Build, Component, Element, Host, Prop, State, h } from '@stencil/core';

const COMPONENT_NAME = 'sn-navbar-type-1';

/**
 * Navbar component for SignNow brand navigation
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Navbar component for site navigation with dropdown menus
 * @slot area-logo - {required}, <me-logo src="..." href="/" alt="..."> - Logo area
 * @slot area-navbar - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Desktop navigation items (also supports me-dropdown)
 * @slot area-actions - {required}, {multiple}, <me-button variant="primary" href="..."> - Desktop action buttons
 * @slot area-navbar-mobile - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Mobile navigation items
 * @slot area-actions-mobile - {required}, {multiple}, <me-button variant="secondary" href="..."> - Mobile action buttons
 * @slot area-search - Optional search component override. Uses sn-navbar-search-type-1 by default.
 * @example
 *   <sn-navbar-type-1>
 *     <me-logo slot="area-logo" href="/" src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate SignNow" width="124" height="42"></me-logo>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/integrations">Integrations</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="https://snseats.signnow.com/pricing">Pricing</me-button>
 *     <me-button slot="area-actions" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="https://support.signnow.com">Support</me-button>
 *     <me-button slot="area-actions-mobile" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions-mobile" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *   </sn-navbar-type-1>
 */
@Component({
  tag: 'sn-navbar-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNNavbarType1 {
  @Element() el: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Tracks if component is ready on client
   */
  @State() isClientReady: boolean = false;

  /**
   * Promise that resolves once the navbar's fetched image assets (e.g. the
   * slotted me-logo, which is a network SVG rather than inline) have been warmed
   * into the browser cache. Started as early as possible so the download runs in
   * parallel with hydration and is ready before the swap.
   */
  private assetsPreloaded?: Promise<void>;

  componentDidLoad() {
    // SSR Overlay Transition Strategy:
    // 1. On SSR: render empty Host with --ssr-deferred class (hidden via CSS)
    // 2. componentDidLoad: set isClientReady → triggers re-render with full navbar + --loading class
    // 3. componentDidUpdate: start transition to visible and hide placeholder
    if (Build.isBrowser) {
      // Kick off image-asset warming immediately (the slotted me-logo lives in
      // this host's light DOM even before the full navbar renders), so the logo
      // image downloads in parallel with hydration instead of only starting when
      // the navbar is revealed.
      this.assetsPreloaded = this.preloadNavbarImageAssets();
      this.isClientReady = true;
    }
  }

  componentDidUpdate() {
    if (
      Build.isBrowser &&
      this.isClientReady &&
      this.el.classList.contains('sn-navbar-type-1--loading') &&
      !this.el.classList.contains('sn-navbar-type-1--ready') &&
      !this.el.classList.contains('sn-navbar-type-1--final')
    ) {
      this.transitionToReady();
    }
  }

  private transitionToReady() {
    const innerNavbar = this.el.querySelector('navbar-type-1');
    // Match both placeholder versions so the swap works on legacy pages
    // (sn-navbar-placeholder-type-1) and new pages (sn-navbar-placeholder-type-2).
    const placeholders = document.querySelectorAll('sn-navbar-placeholder-type-1, sn-navbar-placeholder-type-2');

    const makeReady = () => {
      const dropdowns = this.el.querySelectorAll('me-dropdown');
      const allDropdownsHydrated = Array.from(dropdowns).every(d => d.classList.contains('hydrated'));

      if (!allDropdownsHydrated && dropdowns.length > 0) {
        requestAnimationFrame(() => makeReady());
        return;
      }

      // Gate the swap on the image assets warmed in componentDidLoad. While the
      // navbar is display:none its me-logo IntersectionObserver never fires, so
      // the logo image would otherwise only start loading once the navbar is
      // revealed — showing the real navbar with a missing/late logo (pop-in).
      // Waiting here means it paints instantly on reveal. Capped by a timeout
      // inside the preload so a slow/failed asset never blocks the transition.
      (this.assetsPreloaded ?? this.preloadNavbarImageAssets()).then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              // Atomic swap: reveal the real navbar and hide the placeholder in
              // the same task so there is no visible gap between them.
              placeholders.forEach(p => p.setAttribute('hidden', ''));
              this.el.classList.remove('sn-navbar-type-1--loading');
              this.el.classList.add('sn-navbar-type-1--ready');
            }, 50);
          });
        });
      });
    };

    if (innerNavbar?.classList.contains('hydrated')) {
      makeReady();
    } else if (innerNavbar) {
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (innerNavbar.classList.contains('hydrated')) {
              observer.disconnect();
              makeReady();
              break;
            }
          }
        }
      });
      observer.observe(innerNavbar, { attributes: true, attributeFilter: ['class'] });

      setTimeout(() => {
        observer.disconnect();
        if (!this.el.classList.contains('sn-navbar-type-1--ready') && !this.el.classList.contains('sn-navbar-type-1--final')) {
          makeReady();
        }
      }, 2000);
    }
  }

  /**
   * Warm the browser cache for the navbar's fetched image assets (currently the
   * slotted me-logo image) so they paint instantly when the navbar is revealed.
   * Skips lazy assets inside closed dropdown menus. Resolves when every asset has
   * loaded/errored or after a short safety timeout, whichever comes first.
   */
  private preloadNavbarImageAssets(): Promise<void> {
    if (!Build.isBrowser) return Promise.resolve();

    const urls = new Set<string>();
    this.el.querySelectorAll('me-logo[src]').forEach(el => {
      const src = el.getAttribute('src');
      if (!src) return;
      urls.add(src);
      // me-logo only sets its inner <img src> once its IntersectionObserver
      // fires, which never happens while the navbar is display:none. Set the src
      // eagerly so the image is already present and downloading (images load even
      // inside display:none) well before the reveal — no post-swap pop-in.
      // me-logo re-rendering later with the same URL is a no-op.
      const logoImg = el.querySelector('img');
      if (logoImg && !logoImg.getAttribute('src')) {
        logoImg.setAttribute('src', src);
      }
    });
    this.el.querySelectorAll('img[src]').forEach(el => {
      if ((el as HTMLElement).closest('.me-dropdown__menu')) return;
      const src = el.getAttribute('src');
      if (src) urls.add(src);
    });

    if (urls.size === 0) return Promise.resolve();

    return new Promise<void>(resolve => {
      let pending = urls.size;
      const done = () => {
        if (--pending <= 0) resolve();
      };
      urls.forEach(url => {
        const img = new Image();
        img.onload = done;
        img.onerror = done;
        img.src = url;
      });
      // Safety cap — never block the swap on a slow / failed asset.
      setTimeout(resolve, 800);
    });
  }

  private setSearchOpened(opened: boolean) {
    this.el.classList.toggle('sn-navbar-type-1--search-opened', opened);
  }

  private handleSearchOpenChange = (event: CustomEvent<{ opened: boolean }>) => {
    this.setSearchOpened(event.detail.opened);
  };

  private handleNavbarClick = (event: MouseEvent) => {
    if (!Build.isBrowser || window.innerWidth <= 960) return;

    const target = event.target as HTMLElement;
    const searchButton = target.closest("me-button[icon='interface--search']");

    if (!searchButton || !this.el.contains(searchButton)) return;

    event.preventDefault();

    const search = this.el.querySelector('sn-navbar-search-type-1') as (HTMLElement & { opened?: boolean }) | null;
    const nextOpened = !search?.opened;

    if (search) {
      search.opened = nextOpened;
    }

    this.setSearchOpened(nextOpened);
  };

  private hasCustomSearchSlot() {
    return Array.from(this.el.children).some(child => child.getAttribute('slot') === 'area-search' && !child.hasAttribute('data-default-navbar-search'));
  }

  render() {
    // SSR: render empty Host — placeholder handles SSR structure
    if (!this.isClientReady) {
      return <Host class="sn-navbar-type-1--ssr-deferred" data-color-mode={this.colorMode}></Host>;
    }

    return (
      <Host class={`${COMPONENT_NAME} ${COMPONENT_NAME}--loading`} data-color-mode={this.colorMode} onSnSearchOpenChange={this.handleSearchOpenChange}>
        <navbar-type-1 class={`${COMPONENT_NAME}--nav`} layout="logo-nav-first" sticky="always" mobileWidth="full" mobileBreakpoint={960} border={false} onClick={this.handleNavbarClick}>
          <slot name="area-logo"></slot>
          <slot name="area-navbar"></slot>
          <slot name="area-actions"></slot>
          <slot name="area-navbar-mobile"></slot>
          <slot name="area-actions-mobile"></slot>
          <slot name="area-search"></slot>
          {!this.hasCustomSearchSlot() && (
            <sn-navbar-search-type-1
              slot="area-search"
              data-default-navbar-search
              colorMode={this.colorMode}
              placeholder="Find anything (ie. integration, solutions, etc.)"
              searchLabel="Search"
              startSearchingLabel="to start searching"
              searchErrorLabel="This field may not be empty."
              suggestions={[
                { label: 'Edit PDFs online', href: 'https://www.signnow.com/features/write-on-pdf-document' },
                { label: 'Send documents for signing', href: 'https://www.signnow.com/features/send-document-for-signing-with-a-click-of-a-button' },
                { label: 'Create reusable templates', href: 'https://www.signnow.com/features/create-templates-of-your-documents' },
                { label: 'Send documents in bulk', href: 'https://www.signnow.com/features/bulk-send-documents-for-electronic-signature' },
                { label: 'All features', href: 'https://www.signnow.com/features' },
              ]}
            ></sn-navbar-search-type-1>
          )}
        </navbar-type-1>
      </Host>
    );
  }
}
